import Box from '@mui/material/Box'
import Chart, { ChartConfiguration } from 'chart.js/auto'
import { useEffect, useRef } from 'react'

import 'chartjs-adapter-moment'

import ChartContainer from './ChartContainer'
import { barChartOptions, TooltipHeaderItem } from './ChartsConfig'

export type BarChartData = {
    labels: string[]
    datasets: {
        data: number[]
        backgroundColor: string
        maxBarThickness?: number
        barThickness?: number
    }[]
}

// Make sure that the data being consumed is all of the same type (data, dataColours, dataLabels and labelOrdering).
type ValidDatasetKey<T> = T extends string ? T : never
type BarChartProps<T> = React.PropsWithChildren<
    {
        chartData: BarChartData
        showTooltipLabels: boolean
        label: string
    } & BarChartBaseProps<T>
>

// Shared base type with common properties
export type BarChartBaseProps<T> = {
    dataLabels?: { [key in ValidDatasetKey<T>]: string }
    labelOrdering?: (a: ValidDatasetKey<T>, b: ValidDatasetKey<T>) => number
    skipZeroOnTooltip?: boolean
    reverseTooltipOrder?: boolean
    dataColors?: Record<string, string>
    showTooltipColors?: boolean
    getTooltipHeaderItems?: (date: string) => TooltipHeaderItem[]
    chartHeight?: number
    /** Reserves left-edge space so the first bar doesn't crowd the Y-axis label. Defaults to none */
    padLeft?: boolean
}

export const BarChart = <T,>({
    dataLabels,
    labelOrdering,
    skipZeroOnTooltip,
    reverseTooltipOrder,
    chartData,
    label,
    showTooltipLabels,
    dataColors,
    showTooltipColors,
    getTooltipHeaderItems,
    chartHeight = 400,
    padLeft,
}: BarChartProps<T>) => {
    const canvasEl = useRef<HTMLCanvasElement | null>(null)

    useEffect(() => {
        const ctx = canvasEl.current?.getContext('2d')
        const maxStackedValue = Math.max(
            ...chartData.labels.map((_, labelIndex) => {
                return chartData.datasets.reduce(
                    (sum, dataset) => sum + dataset.data[labelIndex],
                    0,
                )
            }),
        )
        const orderedLabelTranslation =
            labelOrdering && dataLabels
                ? (Object.keys(dataLabels) as (keyof typeof dataLabels)[])
                      .sort(labelOrdering)
                      .map((l) => dataLabels[l])
                : undefined

        const config = {
            type: 'bar',
            data: chartData,
            options: barChartOptions(
                label,
                showTooltipLabels,
                maxStackedValue,
                skipZeroOnTooltip,
                orderedLabelTranslation,
                reverseTooltipOrder ?? false,
                dataColors,
                showTooltipColors,
                getTooltipHeaderItems,
                padLeft,
            ) as any,
        }
        if (ctx) {
            const barChart = new Chart(ctx, config as ChartConfiguration)

            return () => {
                barChart.destroy()
            }
        }
    }, [chartData, padLeft])

    return (
        <ChartContainer>
            <Box sx={{ height: `${chartHeight}px` }} id={'stacked-bar-chart'}>
                <canvas id="emissionsChart" ref={canvasEl} height="100" />
            </Box>
        </ChartContainer>
    )
}

export default BarChart
